import { Stack } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetDefaultVendors } from 'contacts/hooks';
import { IDefaultVendor } from 'contacts/types';
import { Loader } from 'core/elements';
import { Direction, Size } from 'theme/enums';

import { VendorInventoryCard } from './VendorInventoryCard';

interface IProps {
  partNumber?: string;
  manufacturerId?: number;
  enabled?: boolean;
  firstCardLabel: string;
  firstCardTotal?: number | null;
  firstCardLoading?: boolean;
  uom?: string;
  selectedVendorId: number | null;
  computedVendorTotals?: Record<number, number>;
  onSelectVendor: (vendor: IDefaultVendor | null) => void;
}

const loaderSx = { width: 'auto', margin: 0, alignSelf: 'center' };

export const InventoryCards = ({
  partNumber,
  manufacturerId,
  enabled = true,
  firstCardLabel,
  firstCardTotal,
  firstCardLoading,
  uom,
  selectedVendorId,
  computedVendorTotals,
  onSelectVendor,
}: IProps) => {
  const { t } = useTranslation();

  const queryEnabled = enabled && !!partNumber && !!manufacturerId;

  const requestPayload = useMemo(
    () => ({ filters: { partNumber, manufacturerId, onlyWithInventoryEnabled: 1 } }),
    [partNumber, manufacturerId]
  );

  const { records, isLoading } = useGetDefaultVendors(requestPayload, {
    enabled: queryEnabled,
  });

  return (
    <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2 }}>
      <VendorInventoryCard
        groupName={firstCardLabel}
        total={firstCardTotal}
        uom={uom}
        loading={firstCardLoading}
        selected={selectedVendorId === null}
        onClick={() => onSelectVendor(null)}
      />
      {records?.map((vendor) => (
        <VendorInventoryCard
          key={vendor.id}
          groupName={vendor.groupName}
          vendorName={vendor.name}
          total={vendor.totalInventory ?? computedVendorTotals?.[vendor.id]}
          uom={vendor.uom}
          computeHint={t('labelClickToSeeInventory')}
          selected={selectedVendorId === vendor.id}
          onClick={() => onSelectVendor(vendor)}
        />
      ))}
      {isLoading && <Loader size={Size.SM} style={loaderSx} />}
    </Stack>
  );
};
